/**
 *
 * useCheckout
 * Redirect to the payment page, tracking the status of the request so the
 * form can give feedback instead of silently doing nothing.
 * Status is one of 'idle', 'busy', 'unauthorized' or 'failed'.
 *
 */

import { useEvent } from 'react-use';
import useSafeState from 'utils/useSafeState';
import { reportError } from 'utils/Sentry';
import startCheckout from './startCheckout';

const AUTH_STATUSES = new Set([401, 403]);

function failureStatus(err) {
  return AUTH_STATUSES.has(err?.response?.status) ? 'unauthorized' : 'failed';
}

function useCheckout() {
  const [status, setStatus] = useSafeState('idle');

  // Going back from the payment page may restore this page from the
  // back/forward cache, with the status still busy
  useEvent('pageshow', ev => {
    if (ev.persisted) {
      setStatus('idle');
    }
  });

  function handleFailure(err, options) {
    reportError(err, {
      status: err?.response?.status,
      recurring: options.recurring,
    });
    setStatus(failureStatus(err));
  }

  async function checkout(options) {
    if (status === 'busy') {
      return;
    }
    setStatus('busy');
    try {
      window.location = await startCheckout(options);
      // Stay busy while the browser navigates to the payment page
    } catch (err) {
      handleFailure(err, options);
    }
  }

  return [status, checkout];
}

export default useCheckout;
